import {TreeNodeProps, useDropdown} from '@priority-software/priority-style-react';
import {ForwardedRef, useCallback, useImperativeHandle, useState} from 'react';
import {ITreeDropdownProps} from '../parts/TreeDropdown/TreeDropdown';
import {useEventCallback} from 'usehooks-ts';
import {TPersonalizeDropdownHandle} from './types';
import {useEmailContext} from '../../../../../context';
import {isRTL} from '../../../../../../../Localization';

export const useTreeDropdown = ({
    setPersonalizeStates,
    ref
}: ITreeDropdownProps & {ref: ForwardedRef<TPersonalizeDropdownHandle>}) => {
    const {states} = useEmailContext();
    const [searchValue, setSearchValue] = useState('');
    const [branches, setBranches] = useState<TreeNodeProps[]>([states.treeValues]);
    const [selectedBranch, setSelectedBranch] = useState<TreeNodeProps | null>(null);

    useImperativeHandle(ref, () => ({
        resetTreeDropdown: () => setSelectedBranch(null)
    }));

    const onClose = useCallback(() => {
        setSearchValue('');
        setBranches(setExpandedPath([states.treeValues], ''));
    }, []);

    const {isOpen, toggleOpen, closeDropdown, openDropdown} = useDropdown({
        onClose
    });

    const setExpandedPath = (nodes: TreeNodeProps[], searchTerm: string): TreeNodeProps[] => {
        return nodes.map(node => {
            let shouldExpand = false;
            const updatedNode: TreeNodeProps = {...node};

            if (updatedNode.nodes && updatedNode.nodes.length > 0) {
                updatedNode.nodes = setExpandedPath(updatedNode.nodes, searchTerm);
                // Expand this node if any of its children are expanded
                shouldExpand = updatedNode.nodes.some(child => child.isExpanded);
            }

            if (updatedNode.title.toLowerCase().includes(searchTerm.toLowerCase())) {
                shouldExpand = true;
            }

            if (searchTerm === '') {
                shouldExpand = false;
            }

            updatedNode.isExpanded = updatedNode.isMain ? true : shouldExpand;
            updatedNode.extraData.shouldDisplay = searchTerm ? shouldExpand : true;

            return updatedNode;
        });
    };

    const selectId = (nodes: TreeNodeProps[], id: string): TreeNodeProps | undefined => {
        for (const node of nodes) {
            if (node.id === id) {
                return node;
            }

            if (node.nodes && node.nodes.length > 0) {
                const foundNode = selectId(node.nodes, id);
                if (foundNode) {
                    return foundNode;
                }
            }
        }

        return undefined;
    };

    const handleSelect = useEventCallback((id: string) => {
        const selectedBranch = selectId(branches, id);

        if (selectedBranch && !selectedBranch.nodes) {
            setSelectedBranch(selectedBranch);
            setBranches([states.treeValues]);
            setSearchValue('');
            setPersonalizeStates(prevState => ({...prevState, tree: selectedBranch}));
            toggleOpen();
        }
    });

    const onExpandChangeCont = (branches: TreeNodeProps[], id: string, isExpanded: boolean) => {
        return branches.map(node => {
            if (node.id === id) {
                return {...node, isExpanded};
            } else if (node.nodes) {
                return {...node, nodes: onExpandChangeCont(node.nodes, id, isExpanded)};
            }
            return node;
        });
    };

    const handleExpandChange = useEventCallback((id: string, isExpanded: boolean) => {
        const updatedTree = onExpandChangeCont(branches, id, isExpanded);
        setBranches(updatedTree);
    });

    const onSearchChange = useEventCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value || '');
        setBranches(setExpandedPath([states.treeValues], e.target.value));
    });

    return {
        isOpen,
        toggleOpen,
        closeDropdown,
        openDropdown,
        onSearchChange,
        handleSelect,
        handleExpandChange,
        selectedBranch,
        searchValue,
        branches,
        isRTL: isRTL()
    } as const;
};
